import { LexicalComposer } from '@lexical/react/LexicalComposer';
import * as React from 'react';

import { FlashMessageContext } from '../context/FlashMessageContext';
import { SharedAutocompleteContext } from '../context/SharedAutocompleteContext';
import { SharedHistoryContext } from '../context/SharedHistoryContext';
import Editor from './Editor';
import TwygsEditorNodes from '../nodes/TwygsEditorNodes';
import { TableContext } from '../plugins/TablePlugin';
import { useAtom } from 'jotai';
import { articlePayloadAtom, stepAtom } from '../../../components/CreateArticle/atom';

import { useSearchParams } from 'react-router-dom';
import { profileAtom } from '../../../components/Settings/atom';
import { contributorAtom } from '../../../components/Common/Form/textarea-custom-hook-form/atom';

import './index.css';
import { ParagraphNode } from 'lexical';
import { CommentableParagraphNode } from '../nodes/CommentableParagraphNode';
import getTheme from '../themes/TwygsEditorTheme';
import useConvertLexicalScope from '../hooks/useConvertLexicalScope';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useAutoSave } from './hooks/useAutoSave';
import { usePublishArticle, MissingField, FieldErrors } from './hooks/usePublishArticle';
import { useArticleEditorModals } from './hooks/useArticleEditorModals';
import { collapseDesktopAtom, sidebarCollapsedAtom } from '../plugins/TableOfContentsPlugin/atom';
import { CommentableListNode } from '../nodes/CommentableListNode';
import { ListNode } from '@lexical/list';
import ArticleEditorSidebar from './components/ArticleEditorSidebar';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

function Index() {
  const [articlePayload, setArticlePayload] = useAtom(articlePayloadAtom);
  const [profile] = useAtom(profileAtom);
  const [step, setStep] = useAtom(stepAtom);
  const [contributors] = useAtom(contributorAtom);
  const [collapseDesktop] = useAtom(collapseDesktopAtom);
  const [sidebarCollapsed] = useAtom(sidebarCollapsedAtom);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState({ publish: false, saveDraft: false });
  const [modal, setModal] = useState('');
  const [missingFields, setMissingFields] = useState<MissingField[]>([]);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [articleContent, setArticleContent] = useState('');

  const isEdit = searchParams.get('action') === 'click';

  const editorStateRef = useRef(null);
  const textLengthRef = useRef(0);

  // Validation schema
  const metadataSchema = yup.object().shape({
    title: yup.string().trim().max(100, 'Title maximum 100 characters').nullable(),
    description: yup.string().trim().max(2000, 'Description maximum 2000 characters').nullable(),
    isEnableAISummaries: yup.boolean().nullable()
  });
  const methods = useForm({
    resolver: yupResolver(metadataSchema),
    defaultValues: {
      title: articlePayload?.title || '',
      description: articlePayload?.description || '',
      isEnableAISummaries: articlePayload?.isEnableAISummaries || false
    }
  });

  // Handle article content changes from the editor
  const handleArticleContentChange = useCallback((content: string) => {
    setArticleContent(content);
  }, []);

  const { saveStatus, lastSaved, articleId, triggerSave, forceSave } = useAutoSave({
    editorStateRef,
    textLengthRef,
    metadata: {
      ...articlePayload,
      articleId: searchParams.get('id') || articlePayload?.id
    },
    contributors,
    profile,
    searchParams
  });

  useEffect(() => {
    if (articleId && articleId !== articlePayload?.id) {
      setArticlePayload((prevPayload) => ({
        ...prevPayload,
        id: articleId
      }));
    }
  }, [articleId, articlePayload?.id, setArticlePayload]);

  const initialEditorState = JSON.stringify(articlePayload?.content?.editorState);

  const initialConfig = {
    editorState: initialEditorState,
    namespace: 'Twygs',
    nodes: [
      ...TwygsEditorNodes,
      CommentableParagraphNode,
      {
        replace: ParagraphNode,
        with: () => new CommentableParagraphNode()
      },
      // {
      //   replace: HeadingNode,
      //   with: () => new CommentableHeadingNode()
      // },
      {
        replace: ListNode,
        with: (node) => {
          if (node instanceof ListNode) {
            return new CommentableListNode(node.getListType(), node.getStart());
          }
          return new CommentableListNode();
        }
      }
    ],
    onError: (error: Error) => {
      console.log(error);
    },
    theme: getTheme(true)
  };

  const convertLexicalScope = useConvertLexicalScope();

  const { loading: publishLoading, handlePublish } = usePublishArticle({
    articlePayload,
    contributors,
    profile,
    searchParams,
    editorStateRef,
    textLengthRef,
    convertLexicalScope,
    setModal,
    setMissingFields,
    setFieldErrors,
    articleId: searchParams.get('id') || articlePayload?.id
  });

  const handleEditorChange = () => {
    if (publishLoading) return;
    triggerSave();
  };

  const {
    renderPublishModal,
    renderPublishNoMetadataErrorModal,
    renderPublishNoContentErrorModal
  } = useArticleEditorModals({
    setModal,
    step,
    setStep,
    loading,
    handlePublish,
    forceSave,
    triggerSave,
    isEdit,
    missingFields
  });

  useEffect(() => {
    setLoading((prev) => ({ ...prev, publish: publishLoading }));
  }, [publishLoading]);

  // Handle preview toggle
  const handlePreview = useCallback(() => {
    // This will be handled by the Lexical editor's editable state
    // We'll need to access the editor context for this
    setModal('preview');
  }, []);

  // Handle publish action
  const handlePublishClick = useCallback(() => {
    setModal('publish-article');
  }, []);

  // Handle save draft action
  const handleSaveDraft = useCallback(async () => {
    setLoading((prev) => ({ ...prev, saveDraft: true }));
    try {
      await forceSave();
    } finally {
      setLoading((prev) => ({ ...prev, saveDraft: false }));
    }
  }, [forceSave]);

  return (
    <LexicalComposer initialConfig={initialConfig as any}>
      <SharedHistoryContext>
        <TableContext>
          <SharedAutocompleteContext>
            <div
              className={`editor-shell ${collapseDesktop ? 'md:ml-[80px]' : 'md:ml-[256px]'} ${sidebarCollapsed ? 'md:mr-[52px]' : 'md:mr-[520px]'} flex-1 flex flex-col transition-all duration-300`}
            >
              <Editor
                editorStateRef={editorStateRef}
                textLengthRef={textLengthRef}
                onChange={handleEditorChange}
                forceSave={forceSave}
                triggerSave={triggerSave}
                onArticleContentChange={handleArticleContentChange}
              />
              {modal === 'publish-metadata-error' &&
                renderPublishNoMetadataErrorModal(() => setModal(''))}
              {modal === 'publish-content-error' &&
                renderPublishNoContentErrorModal(() => setModal(''))}
              {modal === 'publish-article' && renderPublishModal(() => setModal(''))}
            </div>
            <ArticleEditorSidebar
              articleContent={articleContent}
              forceSave={forceSave}
              triggerSave={triggerSave}
              onPreview={handlePreview}
              onPublish={handlePublishClick}
              onSaveDraft={handleSaveDraft}
              isPublishing={loading.publish}
              isSavingDraft={loading.saveDraft}
              isEdit={isEdit}
              saveStatus={saveStatus}
              lastSaved={lastSaved}
              methods={methods}
              fieldErrors={fieldErrors}
            />
          </SharedAutocompleteContext>
        </TableContext>
      </SharedHistoryContext>
    </LexicalComposer>
  );
}

export default function ArticleEditor(): JSX.Element {
  return (
    <FlashMessageContext>
      <div className="flex flex-col md:flex-row">
        <Index />
      </div>
    </FlashMessageContext>
  );
}
